import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  padding: var(--space-12) var(--space-4);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: var(--text-4xl);
  color: var(--primary-900);
  margin-bottom: var(--space-4);
  font-family: var(--font-serif);

  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const PageSubtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--neutral-700);
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-4);
  }
`;

const LastUpdated = styled.p`
  font-size: var(--text-sm);
  color: var(--neutral-600);
  margin-bottom: var(--space-8);
  text-align: center;
  font-style: italic;
`;

const ContentBlock = styled.div`
  margin-bottom: var(--space-8);

  h2 {
    font-size: var(--text-2xl);
    color: var(--neutral-900);
    margin-bottom: var(--space-4);
    font-family: var(--font-serif);
  }

  h3 {
    font-size: var(--text-xl);
    color: var(--neutral-800);
    margin-bottom: var(--space-3);
    margin-top: var(--space-6);
  }

  p {
    font-size: var(--text-base);
    line-height: 1.8;
    color: var(--neutral-800);
    margin-bottom: var(--space-4);
  }

  ul {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);

    li {
      font-size: var(--text-base);
      color: var(--neutral-800);
      margin-bottom: var(--space-2);
      line-height: 1.8;
    }
  }
`;

const ContactBox = styled.div`
  background: var(--neutral-100);
  border: 1px solid var(--neutral-200);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  margin: var(--space-8) 0;

  h3 {
    font-size: var(--text-lg);
    color: var(--neutral-900);
    margin-bottom: var(--space-3);
  }

  p {
    font-size: var(--text-base);
    color: var(--neutral-700);
    margin: 0;

    a {
      color: var(--primary-600);
      font-weight: 500;
    }
  }
`;

const PrivacyPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>Privacy Policy</PageTitle>
          <PageSubtitle>
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </PageSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <LastUpdated>Last updated: December 2024</LastUpdated>

        <ContentBlock>
          <h2>Introduction</h2>
          <p>
            At Intellica ("we," "us," or "our"), we are committed to protecting your privacy and ensuring 
            the security of your personal information. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you visit our website intellica.it.com.
          </p>
          <p>
            By using our website, you agree to the collection and use of information in accordance with 
            this policy. If you do not agree with the terms of this privacy policy, please do not access 
            the site.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Information We Collect</h2>
          
          <h3>Information You Provide to Us</h3>
          <p>We may collect information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Subscribe to our newsletter or email updates</li>
            <li>Contact us through email or contact forms</li>
            <li>Participate in surveys or feedback requests</li>
            <li>Comment on articles or participate in discussions</li>
            <li>Submit content or article suggestions</li>
          </ul>

          <h3>Information Automatically Collected</h3>
          <p>When you visit our website, we may automatically collect certain information, including:</p>
          <ul>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>IP address</li>
            <li>Pages visited and time spent on each page</li>
            <li>Referring website addresses</li>
            <li>Date and time of visit</li>
            <li>Other standard server log information</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>To provide and maintain our website and services</li>
            <li>To send you newsletters and updates (if you've subscribed)</li>
            <li>To respond to your inquiries and provide customer support</li>
            <li>To improve our website and content based on user feedback</li>
            <li>To analyze website usage and optimize user experience</li>
            <li>To protect against unauthorized access and ensure website security</li>
            <li>To comply with legal obligations and enforce our terms of service</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience on our website. 
            Cookies are small data files stored on your device that help us remember your preferences 
            and understand how you use our site.
          </p>
          
          <h3>Types of Cookies We Use</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements and track their effectiveness</li>
          </ul>
          
          <p>
            You can control cookie settings through your browser preferences. However, disabling certain 
            cookies may limit some features of our website.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Third-Party Services</h2>
          <p>
            We may use third-party services for analytics, advertising, and other purposes. These services 
            may collect information about your use of our website and other websites over time. Third-party 
            services we use include:
          </p>
          <ul>
            <li>Google Analytics for website traffic analysis</li>
            <li>Taboola for content recommendations and advertising</li>
            <li>Social media platforms for content sharing</li>
            <li>Email service providers for newsletter distribution</li>
          </ul>
          <p>
            These third parties have their own privacy policies, and we encourage you to review them.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal 
            information against unauthorized access, alteration, disclosure, or destruction. However, 
            no method of transmission over the internet or electronic storage is 100% secure, and we 
            cannot guarantee absolute security.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Your Rights and Choices</h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
            <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided below.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Children's Privacy</h2>
          <p>
            Our website is not intended for children under 13 years of age. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and believe we 
            have collected information from a child under 13, please contact us immediately.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country 
            of residence. These countries may have data protection laws that are different from the laws 
            of your country. By using our website, you consent to such transfers.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or 
            for legal, operational, or regulatory reasons. We will notify you of any material changes by 
            posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </ContentBlock>

        <ContactBox>
          <h3>Contact Us</h3>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data 
            practices, please contact us at:
          </p>
          <p>
            Email: <a href="mailto:privacy@intellica.it.com">privacy@intellica.it.com</a>
          </p>
          <p>
            Address: Intellica.it.com<br />
            Data Protection Officer<br />
            Privacy Inquiries
          </p>
        </ContactBox>

        <ContentBlock>
          <p>
            By continuing to use our website, you acknowledge that you have read and understood this 
            Privacy Policy and agree to its terms.
          </p>
        </ContentBlock>
      </ContentSection>
    </PageContainer>
  );
};

export default PrivacyPage;